/*
 * Copyright (C) 2019 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { Component } from 'react';
import { arrayOf, func, objectOf, shape, string, element, oneOfType } from 'prop-types';
import { StyleSheet, css } from 'aphrodite';
import { Alert } from '@instructure/ui-alerts';
import { Button, IconButton } from '@instructure/ui-buttons';
import { Flex } from '@instructure/ui-flex';
import { IconTrashLine } from '@instructure/ui-icons';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { Text } from '@instructure/ui-text';
import { View } from '@instructure/ui-view';
import formatMessage from '../format-message';
import CanvasSelect from '../shared/CanvasSelect';
import { CC_FILE_MAX_BYTES } from '../shared/constants';
export default class ClosedCaptionCreatorRow extends Component {
  constructor(props) {
    super(props);
    this.styles = StyleSheet.create({
      messageErrorContainer: {
        position: 'relative',
        minWidth: '350px'
      },
      messageErrorContent: {
        marginTop: '0.5rem',
        position: 'absolute',
        botton: 0,
        left: 0
      }
    });
    this._langSelectRef = /*#__PURE__*/React.createRef();
    this._deleteCCBtnRef = /*#__PURE__*/React.createRef();

    this.handleLanguageChange = (event, selectedLang) => {
      this.props.onLanguageSelected(this.props.languages.find(l => l.id === selectedLang));
    };

    this.handleDeleteRow = _e => {
      this.props.onDeleteRow(this.props.selectedLanguage.id);
    };

    this.handleUploadClosedCaption = event => {
      const uploadedCCFileSize = event.target.files[0].size;
      const maxCCFileSize = CC_FILE_MAX_BYTES;

      if (maxCCFileSize && uploadedCCFileSize > maxCCFileSize) {
        this.props.onFileSelected(null);
        const fileSizeMessageError = formatMessage('The selected file exceeds the {maxSize} Byte limit', {
          maxSize: maxCCFileSize
        });
        this.setState({
          isValidCC: false,
          messageErrorCC: fileSizeMessageError
        });
      } else {
        this.props.onFileSelected(event.target.files[0]);
        this.setState({
          isValidCC: true,
          messageErrorCC: ''
        });
      }
    };

    this.state = {
      isValidCC: true,
      messageErrorCC: ''
    };
  }

  get isReadonly() {
    return this.props.selectedFile && this.props.selectedLanguage;
  }

  focus() {
    if (this._langSelectRef.current) {
      this._langSelectRef.current.focus();
    } else if (this._deleteCCBtnRef.current) {
      this._deleteCCBtnRef.current.focus();
    }
  }

  renderChoosing() {
    return /*#__PURE__*/React.createElement(Flex, {
      as: "div",
      wrap: "wrap",
      justifyItems: "start",
      alignItems: "end",
      "data-testid": "CC-CreatorRow-choosing"
    }, this.renderSelectLanguage(), this.renderChooseFile());
  }

  renderSelectLanguage() {
    var _this$props$selectedL;

    const {
      CLOSED_CAPTIONS_SELECT_LANGUAGE
    } = this.props.uploadMediaTranslations.UploadMediaStrings;
    return /*#__PURE__*/React.createElement(Flex.Item, {
      margin: "0 small small 0"
    }, /*#__PURE__*/React.createElement(CanvasSelect, {
      ref: this._langSelectRef,
      value: (_this$props$selectedL = this.props.selectedLanguage) === null || _this$props$selectedL === void 0 ? void 0 : _this$props$selectedL.id,
      label: /*#__PURE__*/React.createElement(ScreenReaderContent, null, CLOSED_CAPTIONS_SELECT_LANGUAGE),
      liveRegion: this.props.liveRegion,
      onChange: this.handleLanguageChange,
      placeholder: CLOSED_CAPTIONS_SELECT_LANGUAGE,
      translatedStrings: this.props.uploadMediaTranslations.SelectStrings,
      mountNode: this.props.mountNode
    }, this.props.languages.map(o => {
      return /*#__PURE__*/React.createElement(CanvasSelect.Option, {
        key: o.id,
        id: o.id,
        value: o.id
      }, o.label);
    })));
  }

  renderChooseFile() {
    const {
      NO_FILE_CHOSEN,
      SUPPORTED_FILE_TYPES,
      CLOSED_CAPTIONS_CHOOSE_FILE
    } = this.props.uploadMediaTranslations.UploadMediaStrings;
    return /*#__PURE__*/React.createElement(Flex.Item, {
      margin: "0 small small 0"
    }, /*#__PURE__*/React.createElement("input", {
      id: "attachmentFile",
      accept: ".vtt, .srt",
      ref: element => {
        this.fileInput = element;
      },
      onChange: this.handleUploadClosedCaption,
      style: {
        display: 'none'
      },
      type: "file"
    }), /*#__PURE__*/React.createElement(View, {
      as: "div"
    }, /*#__PURE__*/React.createElement(Text, {
      as: "div"
    }, SUPPORTED_FILE_TYPES), /*#__PURE__*/React.createElement(Button, {
      id: "attachmentFileButton",
      onClick: () => {
        this.fileInput.click();
      },
      ref: element => {
        this.attachmentFileButton = element;
      }
    }, this.props.selectedFile ? this.props.selectedFile.name : CLOSED_CAPTIONS_CHOOSE_FILE, /*#__PURE__*/React.createElement(ScreenReaderContent, null, this.state.messageErrorCC)), !this.props.selectedFile && /*#__PURE__*/React.createElement(View, {
      display: "inline-block",
      margin: "0 0 0 small"
    }, /*#__PURE__*/React.createElement(Text, {
      color: "secondary"
    }, NO_FILE_CHOSEN)), !this.state.isValidCC && /*#__PURE__*/React.createElement(View, {
      as: "div",
      className: css(this.styles.messageErrorContainer)
    }, /*#__PURE__*/React.createElement("div", {
      className: css(this.styles.messageErrorContent)
    }, /*#__PURE__*/React.createElement(Text, {
      color: "danger"
    }, this.state.messageErrorCC), /*#__PURE__*/React.createElement(Alert, {
      variant: "error",
      screenReaderOnly: true,
      isLiveRegionAtomic: true,
      liveRegion: this.props.liveRegion
    }, this.state.messageErrorCC)))));
  }

  renderChosen() {
    const {
      REMOVE_FILE
    } = this.props.uploadMediaTranslations.UploadMediaStrings;
    return /*#__PURE__*/React.createElement(Flex, {
      as: "div",
      wrap: "wrap",
      justifyItems: "start",
      alignItems: "end",
      "data-testid": "CC-CreatorRow-chosen"
    }, /*#__PURE__*/React.createElement(Flex.Item, {
      margin: "0 0 small 0"
    }, /*#__PURE__*/React.createElement(View, {
      as: "div",
      borderWidth: "small",
      padding: "0 0 0 small",
      borderRadius: "medium",
      width: "100%"
    }, /*#__PURE__*/React.createElement(Flex, {
      justifyItems: "space-between"
    }, /*#__PURE__*/React.createElement(Flex.Item, null, /*#__PURE__*/React.createElement(Text, {
      weight: "bold"
    }, this.props.selectedLanguage.label)), /*#__PURE__*/React.createElement(Flex.Item, {
      margin: "0 0 0 x-small"
    }, /*#__PURE__*/React.createElement(IconButton, {
      ref: this._deleteCCBtnRef,
      withBackground: false,
      withBorder: false,
      onClick: this.handleDeleteRow,
      screenReaderLabel: formatMessage(REMOVE_FILE, {
        lang: this.props.selectedLanguage.label
      })
    }, /*#__PURE__*/React.createElement(IconTrashLine, null)))))));
  }

  render() {
    return /*#__PURE__*/React.createElement(Flex, {
      as: "div",
      display: "flex",
      direction: "column",
      width: "100%"
    }, this.isReadonly ? this.renderChosen() : this.renderChoosing());
  }

}
ClosedCaptionCreatorRow.propTypes = {
  languages: arrayOf(shape({
    id: string,
    label: string
  })),
  liveRegion: func,
  uploadMediaTranslations: shape({
    UploadMediaStrings: objectOf(string),
    SelectStrings: objectOf(string)
  }),
  onDeleteRow: func,
  onFileSelected: func,
  onLanguageSelected: func,
  selectedFile: shape({
    name: string.isRequired
  }),
  // there's more, but his is all I care about
  selectedLanguage: shape({
    id: string.isRequired,
    label: string.isRequired
  }),
  mountNode: oneOfType([element, func])
};